import { Check, type LucideIcon } from 'lucide-react'

import { cn } from '@/utils/cn'

import { Skeleton } from './Skeleton'
import type { StatTone } from './StatCard'

/** Mesmos tons do `StatCard`, com a variação de "selecionado". */
const TONES: Record<StatTone, { bar: string; icon: string; selected: string }> = {
  brand: {
    bar: 'bg-brand',
    icon: 'bg-brand/10 text-brand',
    selected: 'border-brand/60 bg-brand/5 ring-brand/30',
  },
  progress: {
    bar: 'bg-progress',
    icon: 'bg-progress/12 text-progress',
    selected: 'border-progress/60 bg-progress/5 ring-progress/30',
  },
  success: {
    bar: 'bg-ok',
    icon: 'bg-ok/12 text-ok',
    selected: 'border-ok/60 bg-ok/5 ring-ok/30',
  },
  attention: {
    bar: 'bg-attention',
    icon: 'bg-attention/15 text-[#8a6d05] dark:text-attention',
    selected: 'border-attention/70 bg-attention/8 ring-attention/30',
  },
  critical: {
    bar: 'bg-critical',
    icon: 'bg-critical/12 text-critical',
    selected: 'border-critical/60 bg-critical/5 ring-critical/30',
  },
  idle: {
    bar: 'bg-idle',
    icon: 'bg-idle/15 text-idle',
    selected: 'border-idle/60 bg-idle/5 ring-idle/30',
  },
}

interface KpiFilterCardProps {
  label: string
  /** Número ou texto já formatado (ex.: moeda). */
  value?: number | string
  hint?: string
  icon: LucideIcon
  tone: StatTone
  selected: boolean
  onClick: () => void
  loading?: boolean
  error?: boolean
  /** Destaca o número quando diferente de zero (ex.: termos pendentes). */
  alerta?: boolean
  className?: string
}

/** KPI clicável: aplica (ou remove, se já ativo) o filtro correspondente na lista. */
export function KpiFilterCard({
  label,
  value,
  hint,
  icon: Icon,
  tone,
  selected,
  onClick,
  loading,
  error,
  alerta,
  className,
}: KpiFilterCardProps) {
  const colors = TONES[tone]
  // Valor numérico zero (ou texto vazio) não dispara o destaque.
  const emAlerta = !!alerta && !!value

  return (
    <button
      type="button"
      onClick={onClick}
      aria-pressed={selected}
      className={cn(
        'group relative overflow-hidden rounded-2xl border border-line bg-surface p-4 text-left shadow-xs transition-all',
        'hover:-translate-y-0.5 hover:shadow-md focus-visible:outline-2 focus-visible:outline-offset-2 focus-visible:outline-brand',
        selected && cn('ring-2', colors.selected),
        emAlerta && !selected && 'border-attention/60',
        className,
      )}
    >
      <span aria-hidden className={cn('absolute inset-y-0 left-0 w-1', colors.bar)} />
      <span className="flex items-start justify-between gap-3">
        <span className="min-w-0">
          <span className="block text-xs font-medium text-fg-muted">{label}</span>
          {loading ? (
            <Skeleton className="mt-2 h-6 w-16" />
          ) : error ? (
            <span className="mt-1 block text-sm text-critical">Erro ao carregar</span>
          ) : (
            <span
              className={cn(
                'mt-1 block text-2xl font-bold tabular-nums',
                emAlerta ? 'text-[#8a6d05] dark:text-attention' : 'text-fg',
              )}
            >
              {value ?? '—'}
            </span>
          )}
          <span
            className={cn(
              'mt-0.5 block text-xs',
              selected ? 'font-medium text-fg' : 'text-fg-subtle group-hover:text-fg-muted',
            )}
          >
            {selected ? 'Filtrando a lista' : (hint ?? 'Clique para filtrar')}
          </span>
        </span>
        <span
          className={cn(
            'grid size-9 shrink-0 place-items-center rounded-xl transition-transform group-hover:scale-105',
            colors.icon,
          )}
        >
          {selected ? <Check className="size-4" /> : <Icon className="size-4" />}
        </span>
      </span>
    </button>
  )
}
